import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import { PODCAST_PROVIDER_MAP, type PodcastProvider } from "shared/data/podcastProviders";

export interface PodcastProviderCardProps {
  provider: PodcastProvider;
}

const CARD_CLASSES =
  'group w-full bg-surface-container-low p-4 sm:p-6 md:p-8 rounded-2xl hover:bg-surface-bright transition-colors cursor-pointer flex flex-col items-center gap-2 sm:gap-3 md:gap-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2';

export function PodcastProviderCard({ provider }: PodcastProviderCardProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { icon, label, href, copyable } = PODCAST_PROVIDER_MAP[provider];

  const content = (
    <>
      <span className="opacity-50 group-hover:opacity-100 transition-all text-on-surface [&_svg]:w-8 [&_svg]:h-8 sm:[&_svg]:w-10 sm:[&_svg]:h-10 md:[&_svg]:w-12 md:[&_svg]:h-12">
        {icon}
      </span>
      <span className="font-sans text-xs sm:text-sm tracking-widest font-bold uppercase text-on-surface">
        {label}
        {!copyable && <span className="sr-only">{t('a11y.opensInNewTab')}</span>}
      </span>
    </>
  );

  if (copyable) {
    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(href);
        showToast({
          variant: 'success',
          title: t('toast.rssCopied.title'),
          message: t('toast.rssCopied.message'),
        });
      } catch {
        showToast({
          variant: 'error',
          title: t('toast.rssCopyFailed.title'),
          message: t('toast.rssCopyFailed.message'),
        });
      }
    };

    return (
      <button type="button" onClick={handleCopy} className={CARD_CLASSES}>
        {content}
      </button>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={CARD_CLASSES}
    >
      {content}
    </a>
  );
}
